import { useState } from 'react';

import { Drawer } from '../../../../components/Drawer';
import Dropdown from '../../../../components/Dropdown';
import { HelpDrawerLink } from '../../../../components/HelpDrawerLink';
import { useBlurValidation } from '../../../../hooks/useBlurValidation';
import { PrimaryStep, SubStep, type WorkflowFormData, WorkflowSubStepProps } from '../../config';
import { useApplication, WorkflowAlertVariation } from '../../context';
import { requiredChoice } from '../../utils/fieldValidation';
import DocumentRemovalConfirmationModal from './DocumentRemovalConfirmationModal';
import {
  clearConditionalDocumentFields,
  deleteConditionalDocuments,
  getConditionalDocumentFileNames,
  getConditionalDocuments,
} from './citizenship-document-cleanup';

export interface CitizenshipStatusFormData {
  isUSCitizen: boolean | null;
}

const citizenshipOptions = [
  { label: '- Select an option -', value: null },
  { label: 'Yes', value: true },
  { label: 'No', value: false },
];

function CitizenshipStatusPage({ data, fieldErrors }: WorkflowSubStepProps<CitizenshipStatusFormData>) {
  const [isHelpDrawerOpen, setIsHelpDrawerOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [pendingCitizenshipStatus, setPendingCitizenshipStatus] = useState<boolean | null | undefined>(undefined);
  const { applicationId, clearSubStepCompletions, formData, showWorkflowAlert, updateWorkflowData } = useApplication();
  const baseTestId = 'citizenship-status-page';

  const {
    handleBlur,
    handleChange: handleValidationChange,
    getFieldError,
  } = useBlurValidation({
    externalErrors: fieldErrors,
    formData: data,
    rules: [
      { field: 'isUSCitizen', label: 'Are you a United States (U.S.) Citizen?', validationRules: [requiredChoice] },
    ],
  });

  const updateCitizenshipStatus = (value: boolean | null) => {
    updateWorkflowData((currentFormData: WorkflowFormData) => {
      const nextFormData = clearConditionalDocumentFields(currentFormData, [
        'passport',
        'usVisaDocument',
        'residencyDocument',
        'residencySupportingDocuments',
      ]);
      const citizenshipDetails = nextFormData[PrimaryStep.CITIZENSHIP_DETAILS] ?? {};

      return {
        ...nextFormData,
        [PrimaryStep.CITIZENSHIP_DETAILS]: {
          ...citizenshipDetails,
          [SubStep.CITIZENSHIP_STATUS]: {
            ...(citizenshipDetails[SubStep.CITIZENSHIP_STATUS] ?? data),
            isUSCitizen: value,
          },
          [SubStep.IMMIGRATION_DOCUMENT]: {
            documentType: 'Visa Number',
            immigrationDate: '',
            visaNumber: '',
          },
          [SubStep.PASSPORT_INFORMATION]: {
            passportExpirationDate: '',
            passportIssuanceCountry: '',
            passportIssuanceCountryCode: '',
            passportIssueDate: '',
            passportNumber: '',
            passportType: '',
          },
          [SubStep.RESIDENCY_INFORMATION]: {
            citizenshipCountry: '',
            citizenshipCountryCode: '',
            isPermanentResident: null,
          },
          [SubStep.US_ENTRY_INFORMATION]: {
            entryVisaType: '',
            firstEntryDate: '',
            yearsInUS: '',
          },
        },
      };
    });
    clearSubStepCompletions([
      { stepId: PrimaryStep.CITIZENSHIP_DETAILS, subStepId: SubStep.RESIDENCY_INFORMATION },
      { stepId: PrimaryStep.CITIZENSHIP_DETAILS, subStepId: SubStep.US_ENTRY_INFORMATION },
      { stepId: PrimaryStep.CITIZENSHIP_DETAILS, subStepId: SubStep.IMMIGRATION_DOCUMENT },
      { stepId: PrimaryStep.CITIZENSHIP_DETAILS, subStepId: SubStep.PASSPORT_INFORMATION },
      { stepId: PrimaryStep.DOCUMENT_UPLOAD, subStepId: SubStep.UPLOAD_DOCUMENTS },
    ]);
    handleValidationChange('isUSCitizen', value);
  };

  const handleChange = (value: boolean | null) => {
    if (value === data.isUSCitizen) return;

    const documents = getConditionalDocuments(formData, [
      'passport',
      'usVisaDocument',
      'residencyDocument',
      'residencySupportingDocuments',
    ]);
    if (documents.length > 0) {
      setPendingCitizenshipStatus(value);

      return;
    }

    updateCitizenshipStatus(value);
  };

  const handleConfirmStatusChange = async () => {
    if (pendingCitizenshipStatus === undefined) return;
    const documents = getConditionalDocuments(formData, [
      'passport',
      'usVisaDocument',
      'residencyDocument',
      'residencySupportingDocuments',
    ]);

    setIsDeleting(true);
    try {
      await deleteConditionalDocuments(applicationId, documents);
      updateCitizenshipStatus(pendingCitizenshipStatus);
      setPendingCitizenshipStatus(undefined);
    } catch {
      showWorkflowAlert({
        heading: 'Document Could Not Be Deleted',
        text: 'Your citizenship answer was not changed. Please try again.',
        variation: WorkflowAlertVariation.ERROR,
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div data-testid={baseTestId}>
      <div
        className='ds-u-margin-bottom--3'
        data-testid={`${baseTestId}-citizenship`}>
        <Dropdown
          required
          data-testid={`${baseTestId}-citizenship-dropdown`}
          errorMessage={getFieldError('isUSCitizen')}
          hint='Select whether you are a United States citizen'
          label='Are you a United States (U.S.) Citizen?'
          name='isUSCitizen'
          options={citizenshipOptions}
          value={data.isUSCitizen ?? null}
          onBlur={() => handleBlur('isUSCitizen')}
          onChange={(value: boolean | null) => handleChange(value)}
        />
      </div>

      <div
        className='ds-u-border-top--2 ds-u-padding-top--3 ds-u-margin-top--4 field-max-width'
        data-testid={`${baseTestId}-help`}>
        <HelpDrawerLink
          data-testid={`${baseTestId}-help-link`}
          onClick={() => setIsHelpDrawerOpen(true)}
        />
      </div>

      <Drawer
        data-testid={`${baseTestId}-drawer`}
        heading='Help: My Citizenship Details'
        isOpen={isHelpDrawerOpen}
        onClose={() => setIsHelpDrawerOpen(false)}>
        <div className='ds-u-padding--3'>
          <h3 className='ds-u-margin-top--0'>Get Help for U.S. Citizenship</h3>
          <p>
            Please let us know if you are a United States citizen. We will need this data to verify if you are allowed
            to work for the government.
          </p>

          <h4 className='ds-u-margin-top--3'>Are you a United States (U.S.) Citizen?</h4>
          <p>
            Select "Yes" if you are a U.S. citizen by birth or naturalization. Select "No" if you are not a U.S.
            citizen. If you select "No", you will be asked to provide data and documents related to your immigration
            status.
          </p>

          <p className='ds-u-margin-top--3'>
            <strong>Note:</strong> All applicants must be authorized to work in the United States. Non-citizens must
            provide valid work authorization documents.
          </p>
        </div>
      </Drawer>

      <DocumentRemovalConfirmationModal
        fileNames={getConditionalDocumentFileNames(formData, [
          'passport',
          'usVisaDocument',
          'residencyDocument',
          'residencySupportingDocuments',
        ])}
        isDeleting={isDeleting}
        isOpen={pendingCitizenshipStatus !== undefined}
        onCancel={() => setPendingCitizenshipStatus(undefined)}
        onConfirm={() => void handleConfirmStatusChange()}
      />
    </div>
  );
}

export default CitizenshipStatusPage;
